import { useState } from 'react';
import { Swords, Shield } from 'lucide-react';
import { normalizeClassName } from '@/utils/classNameUtils';

interface ClassIconSimpleProps {
  characterClass: string;
  classType?: 'attacker' | 'supporter';
  size?: number;
}

/**
 * 검은색(다크모드: 흰색) 클래스 아이콘 컴포넌트
 * 역할 색상 없이 중립적인 컨텍스트에서 사용
 */
export default function ClassIconSimple({
  characterClass,
  classType,
  size = 32
}: ClassIconSimpleProps) {
  const [imageError, setImageError] = useState(false);

  // Fallback 아이콘 결정 (classType이 제공된 경우에만)
  const FallbackIcon = () => {
    const iconSize = size * 0.5;

    if (!classType) {
      // classType이 없으면 기본 칼 아이콘
      return <Swords className="w-full h-full" style={{ width: iconSize, height: iconSize }} />;
    }

    // classType에 따라 아이콘 결정 (역할 전환은 고려 안함)
    if (classType === 'attacker') {
      return <Swords className="w-full h-full" style={{ width: iconSize, height: iconSize }} />;
    }
    return <Shield className="w-full h-full" style={{ width: iconSize, height: iconSize }} />;
  };

  // 클래스 이름 정규화 (신의기사 → 발키리, 심판자 → 홀리나이트)
  const normalizedClassName = normalizeClassName(characterClass);

  // 아이콘 경로
  const iconPath = `/class-icons/${normalizedClassName}.svg`;

  // 이미지 로드 실패 시 fallback 표시
  if (imageError) {
    return (
      <div
        className="rounded-full flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
        style={{ width: size, height: size }}
      >
        <FallbackIcon />
      </div>
    );
  }

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <div className="w-full h-full rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        <img
          src={iconPath}
          alt={characterClass}
          className="object-contain filter-neutral"
          style={{ width: size * 0.8, height: size * 0.8 }}
          onError={() => setImageError(true)}
        />
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .filter-neutral {
          filter: brightness(0) saturate(100%) brightness(0.2);
        }
        .dark .filter-neutral {
          filter: brightness(0) saturate(100%) invert(100%);
        }
      ` }} />
    </div>
  );
}
